// src/pages/Dashboard/DashboardPage.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { request } from "../../services/api";
import { GiGreenhouse } from "react-icons/gi";
import { LuHardDriveDownload, LuHardDriveUpload } from "react-icons/lu";
import { FaThermometerHalf, FaTint, FaWind, FaRedo } from "react-icons/fa";
import styles from "./Dashboard.module.css";
import UserMenu from "../../components/UserMenu/UserMenu";
import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import { useLiveSensors } from "../../hooks/useLiveSensors";

// --- تنظیمات رنگ و متن وضعیت‌ها ---
const getStatusConfig = (status) => {
  switch (status) {
    case "online":
      return { color: "#52ae47", text: "آنلاین (زنده)", dotAnim: true };
    case "connecting":
      return { color: "#f59f00", text: "در حال اتصال...", dotAnim: true };
    case "offline":
      return { color: "#ff4d4d", text: "آفلاین (قطع)", dotAnim: false };
    case "suspended":
      return { color: "#718096", text: "توقف تلاش (قطع)", dotAnim: false };
    default:
      return { color: "#718096", text: "نامشخص", dotAnim: false };
  }
};

const getCustomName = (customNames, index, fallback = "") => {
  if (!customNames) return fallback;

  const name = customNames[index] ?? customNames[String(index)];

  if (name === undefined || name === null || String(name).trim() === "") {
    return fallback;
  }

  return String(name).trim();
};

// --- نرمال‌سازی مقدار اتصال ---
const isConnectedValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON" ||
    value === "connected"
  );
};

// --- بررسی اینکه مقدار سنسور واقعاً قابل نمایش هست یا نه ---
const hasValidValue = (value) => {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== "--" &&
    value !== "---" &&
    value !== "N/A" &&
    value !== "nan" &&
    value !== "NaN"
  );
};

// --- بررسی اتصال یا تعریف‌شدن یک سنسور/ورودی/خروجی ---
const isVisibleItem = (item) => {
  if (!item) return false;

  const connectValue =
    item.isConnect ?? item.isconnect ?? item.connected ?? item.is_connected;

  if (connectValue !== undefined && connectValue !== null) {
    return isConnectedValue(connectValue);
  }

  return hasValidValue(item.value);
};

// --- بررسی روشن/فعال بودن ورودی یا خروجی دیجیتال ---
const isActiveValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "on" ||
    value === "ON" ||
    value === "true" ||
    value === "TRUE"
  );
};

const getDigitalDisplayValue = (item) => {
  const value = item?.status ?? item?.value ?? "0";
  return isActiveValue(value) ? "1" : "0";
};

// --- محدودسازی آیتم‌ها برای اینکه کارت شلوغ نشود ---
const MAX_ANALOG_ITEMS_IN_CARD = 6;
const MAX_DIGITAL_ITEMS_IN_CARD = 16;

// --- کامپوننت کارت دستگاه ---
const DeviceCard = ({ device, onClick }) => {
  const { sensors, status, manualRetry } = useLiveSensors(device.device_serial);

  const [customNames, setCustomNames] = useState({
    temp: {},
    humi: {},
    wind: {},
    dIn: {},
    dOut: {},
  });

  useEffect(() => {
    let cancelled = false;

    const loadCustomNames = async () => {
      if (!device?.id) return;

      try {
        const [tempNames, humiNames, windNames, dInNames, dOutNames] =
          await Promise.all([
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=temp`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=humi`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=wind`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=dIn`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=dOut`,
            ),
          ]);

        if (cancelled) return;

        setCustomNames({
          temp: tempNames || {},
          humi: humiNames || {},
          wind: windNames || {},
          dIn: dInNames || {},
          dOut: dOutNames || {},
        });
      } catch (error) {
        console.error("Error loading dashboard custom names:", error);
      }
    };

    loadCustomNames();

    return () => {
      cancelled = true;
    };
  }, [device?.id]);

  const statusConfig = getStatusConfig(status);
  const isSuspended = status === "suspended";
  const isOffline = status === "offline";

  const temps = Array.isArray(sensors?.temps) ? sensors.temps : [];
  const humidity = Array.isArray(sensors?.humidity) ? sensors.humidity : [];
  const dIn = Array.isArray(sensors?.dIn) ? sensors.dIn : [];
  const dOut = Array.isArray(sensors?.dOut) ? sensors.dOut : [];

  const connectedTemps = temps
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const connectedHumidity = humidity
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const windVisible = isVisibleItem(sensors?.wind);

  const connectedDIn = dIn
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const connectedDOut = dOut
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const analogItems = [
    ...connectedTemps.map(({ item, index }) => ({
      key: `temp-${index}`,
      type: "temp",
      label: getCustomName(customNames.temp, index, `دما ${index}`),
      value: item.value,
      unit: "°C",
      setpoint: item.setpoint,
      triggered: item.triggered,
      icon: <FaThermometerHalf />,
    })),

    ...connectedHumidity.map(({ item, index }) => ({
      key: `humi-${index}`,
      type: "humid",
      label: getCustomName(customNames.humi, index, `رطوبت ${index}`),
      value: item.value,
      unit: "%",
      setpoint: item.setpoint,
      triggered: item.triggered,
      icon: <FaTint />,
    })),

    ...(windVisible
      ? [
          {
            key: "wind-0",
            type: "wind",
            label: getCustomName(customNames.wind, 0, "باد"),
            value: sensors.wind.value,
            unit: "km/h",
            setpoint: sensors.wind.setpoint,
            triggered: sensors.wind.triggered,
            icon: <FaWind />,
          },
        ]
      : []),
  ];

  const visibleAnalogItems = analogItems.slice(0, MAX_ANALOG_ITEMS_IN_CARD);
  const hiddenAnalogCount = Math.max(
    analogItems.length - MAX_ANALOG_ITEMS_IN_CARD,
    0,
  );

  const hasAnyVisibleData =
    visibleAnalogItems.length > 0 ||
    connectedDIn.length > 0 ||
    connectedDOut.length > 0;

  const handleRetryClick = (e) => {
    e.stopPropagation();
    if (manualRetry) manualRetry();
  };
  const renderDigitalMiniGrid = (type) => {
    const isInput = type === "dIn";

    const items = (isInput ? connectedDIn : connectedDOut).slice(
      0,
      MAX_DIGITAL_ITEMS_IN_CARD,
    );
    const names = isInput ? customNames.dIn : customNames.dOut;
    const fallbackPrefix = isInput ? "ورودی" : "خروجی";

    return (
      <div className={styles.digitalMiniGrid}>
        {items.map(({ item, index }) => {
          const value = getDigitalDisplayValue(item);
          const fullName = getCustomName(
            names,
            index,
            `${fallbackPrefix} ${index}`,
          );

          return (
            <div
              key={`${type}-mini-${index}`}
              className={styles.digitalMiniCell}
              title={fullName}
              data-tooltip={fullName}
            >
              <span className={styles.digitalMiniIndex}>{index}</span>

              <span
                className={`
                ${styles.digitalMiniValue}
                ${value === "1" ? styles.digitalValueOn : styles.digitalValueOff}
              `}
              >
                {value}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.card} onClick={onClick}>
      <GiGreenhouse className={styles.cardIcon} />

      <div className={styles.cardContent}>
        <h3
          className={styles.deviceName}
          dir="rtl"
          style={{
            direction: "rtl",
            textAlign: "right",
            unicodeBidi: "plaintext",
            width: "100%",
          }}
        >
          {device.device_name}
        </h3>

        <div className={styles.deviceSerialText}>
          سریال: {device.device_serial}
        </div>

        <div
          className={styles.sensorSection}
          style={{
            opacity: isSuspended || isOffline ? 0.5 : 1,
            filter: isSuspended ? "grayscale(100%)" : "none",
            transition: "all 0.3s ease",
          }}
        >
          {!hasAnyVisibleData ? (
            <div className={styles.noSensorState}>
              هنوز سنسور یا ورودی فعالی برای این دستگاه گزارش نشده است
            </div>
          ) : (
            <>
              {visibleAnalogItems.length > 0 && (
                <div className={styles.smartSensorGrid}>
                  {visibleAnalogItems.map((item) => (
                    <div
                      key={item.key}
                      className={`
                        ${styles.smartItem}
                        ${item.type === "temp" ? styles.tempItem : ""}
                        ${item.type === "humid" ? styles.humidItem : ""}
                        ${item.type === "wind" ? styles.windItem : ""}
                      `}
                    >
                      <div className={styles.smartIcon}>{item.icon}</div>

                      <div className={styles.smartText}>
                        <span className={styles.smartLabel}>{item.label}</span>

                        <span
                          className={styles.smartValue}
                          style={{
                            color: item.triggered ? "#ff4d4d" : "#fff",
                          }}
                        >
                          {item.value}
                          <small>{item.unit}</small>
                        </span>

                        {hasValidValue(item.setpoint) && (
                          <span className={styles.smartSet}>
                            حد: {item.setpoint}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hiddenAnalogCount > 0 && (
                <div className={styles.moreSensorsHint}>
                  + {hiddenAnalogCount} سنسور دیگر در صفحه مانیتور
                </div>
              )}

              {(connectedDIn.length > 0 || connectedDOut.length > 0) && (
                <div
                  className={styles.digitalSummaryBox}
                  onClick={(e) => e.stopPropagation()}
                >
                  {connectedDIn.length > 0 && (
                    <div
                      className={`
                        ${styles.digitalSummaryRow}
                        ${styles.digitalInputRow}
                        ${styles.digitalAlwaysOpenRow}
                      `}
                    >
                      <div className={styles.digitalSummaryHeader}>
                        <span className={styles.digitalSummaryTitleWithIcon}>
                          <LuHardDriveDownload
                            className={`
                              ${styles.digitalSummaryIcon}
                              ${styles.digitalInputIcon}
                            `}
                          />

                          <span>ورودی دیجیتال</span>
                        </span>
                      </div>

                      <div
                        className={styles.digitalDetailsCompactArea}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderDigitalMiniGrid("dIn")}
                      </div>
                    </div>
                  )}

                  {connectedDOut.length > 0 && (
                    <div
                      className={`
                        ${styles.digitalSummaryRow}
                        ${styles.digitalOutputRow}
                        ${styles.digitalAlwaysOpenRow}
                      `}
                    >
                      <div className={styles.digitalSummaryHeader}>
                        <span className={styles.digitalSummaryTitleWithIcon}>
                          <LuHardDriveUpload
                            className={`
                              ${styles.digitalSummaryIcon}
                              ${styles.digitalOutputIcon}
                            `}
                          />

                          <span>خروجی دیجیتال</span>
                        </span>
                      </div>

                      <div
                        className={styles.digitalDetailsCompactArea}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderDigitalMiniGrid("dOut")}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            marginTop: "15px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "28px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              className={statusConfig.dotAnim ? styles.statusDot : ""}
              style={{
                backgroundColor: statusConfig.color,
                boxShadow: statusConfig.dotAnim
                  ? `0 0 0 rgba(255,255,255,0.4)`
                  : "none",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                display: "inline-block",
              }}
            ></span>

            <span
              style={{
                fontSize: "12px",
                color: statusConfig.color,
                fontWeight: "bold",
              }}
            >
              {statusConfig.text}
            </span>
          </div>

          {isSuspended && (
            <button
              onClick={handleRetryClick}
              className={styles.retryBtn}
              title="تلاش مجدد برای اتصال"
            >
              <FaRedo />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- تنظیمات retry داشبورد ---
const DASHBOARD_RETRY_BASE_MS = 1000;
const DASHBOARD_RETRY_MAX_MS = 30000;

const getDashboardRetryDelay = (attempt) => {
  const jitter = Math.floor(Math.random() * 700);
  return (
    Math.min(
      DASHBOARD_RETRY_MAX_MS,
      DASHBOARD_RETRY_BASE_MS * Math.pow(2, attempt),
    ) + jitter
  );
};

// --- کامپوننت اصلی صفحه ---
const DashboardPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState("");

  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const isMountedRef = useRef(false);

  const navigate = useNavigate();

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const loadDevices = useCallback(
    async (showLoading = false) => {
      if (!isMountedRef.current) return;

      if (showLoading) {
        setLoading(true);
      }

      try {
        const data = await request("/get_devices.php");

        if (!isMountedRef.current) return;

        if (!data || data.status !== "success") {
          throw new Error(data?.message || "خطا در دریافت لیست دستگاه‌ها");
        }

        setDevices(data.devices || []);
        setConnectionError("");
        retryAttemptRef.current = 0;
        clearRetryTimer();
      } catch (error) {
        if (!isMountedRef.current) return;

        console.error("Error fetching devices:", error);

        setConnectionError(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "اینترنت قطع است. پس از اتصال، لیست دستگاه‌ها خودکار به‌روزرسانی می‌شود."
            : "ارتباط با سرور برقرار نیست. تلاش مجدد خودکار انجام می‌شود...",
        );

        clearRetryTimer();

        const delay = getDashboardRetryDelay(retryAttemptRef.current);

        retryTimerRef.current = setTimeout(() => {
          retryAttemptRef.current += 1;
          loadDevices(false);
        }, delay);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [clearRetryTimer],
  );

  useEffect(() => {
    isMountedRef.current = true;

    const handleOnline = () => {
      retryAttemptRef.current = 0;
      clearRetryTimer();
      setConnectionError("");
      loadDevices(devices.length === 0);
    };

    const handleOffline = () => {
      setConnectionError(
        "اینترنت قطع است. پس از اتصال، لیست دستگاه‌ها خودکار به‌روزرسانی می‌شود.",
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    loadDevices(true);

    return () => {
      isMountedRef.current = false;

      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      clearRetryTimer();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDevices, clearRetryTimer]);

  const handleManualRefresh = () => {
    retryAttemptRef.current = 0;
    clearRetryTimer();
    loadDevices(devices.length === 0);
  };

  const handleCardClick = (device) => {
    if (!device?.id) return;

    const permissions = device.permissions || null;

    if (permissions) {
      sessionStorage.setItem(
        `device_permissions_${device.id}`,
        JSON.stringify(permissions),
      );
    } else {
      sessionStorage.removeItem(`device_permissions_${device.id}`);
    }

    sessionStorage.setItem(`device_${device.id}`, JSON.stringify(device));

    navigate(`/monitor/${device.id}`, {
      state: {
        device,
        permissions,
      },
    });
  };

  if (loading && devices.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        در حال دریافت لیست دستگاه‌ها...
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>داشبورد گلخانه هوشمند</h2>
            <UserMenu dropdownAlign="left" />
          </div>
        </header>

        {connectionError && (
          <div
            style={{
              direction: "rtl",
              margin: "12px 0 16px 0",
              padding: "10px 12px",
              borderRadius: "12px",
              background: "rgba(245, 159, 0, 0.14)",
              border: "1px solid rgba(245, 159, 0, 0.35)",
              color: "#ffd43b",
              fontSize: "13px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span>{connectionError}</span>

            <button
              type="button"
              onClick={handleManualRefresh}
              style={{
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                borderRadius: "10px",
                padding: "6px 10px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "12px",
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaRedo />
              تلاش مجدد
            </button>
          </div>
        )}

        <div className={styles.gridWrapper}>
          {devices.length === 0 ? (
            <div className={styles.emptyState}>
              <p>
                {connectionError
                  ? "در حال تلاش برای دریافت لیست دستگاه‌ها..."
                  : "هیچ دستگاهی یافت نشد."}
              </p>
              <small>
                {connectionError
                  ? "نیازی به refresh صفحه نیست؛ اتصال به‌صورت خودکار بررسی می‌شود."
                  : "لطفاً اتصال دیتابیس را بررسی کنید."}
              </small>
            </div>
          ) : (
            <div className={styles.grid}>
              {devices.map((device) => (
                <DeviceCard
                  key={device.id}
                  device={device}
                  onClick={() => handleCardClick(device)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
